
import api, {
    CATALOG_TYPE, COLUMNS_TYPE, FAVORITES_TYPE, LIST,
    ORGANIZATION_ENTITLEMENTS_TYPE,
    SEARCH_RESULTS_TYPE, WORKSPACE_PRODUCTS_TYPE, WORKSPACES_TYPE
} from '../Api'
import {IActionResponse} from "../../dal/BaseDAL";
import {
    IGetOrganizationRefsResponse,
    IOrganization, IOrganizationEntitlements,
    IOrganizationFeatureSettings, IOrganizationProcurementSettings,
    IOrganizationRef,
    IUpdateOrganizationRequest
} from "../../models/Settings";

import { setSelectedOrganization } from "../settingsSlice";
import {cloneDeep} from "lodash";

const ORGANIZATION_TYPE = 'Organizations';


const organizationsApi = api.injectEndpoints({
    endpoints: (build) => ({
        //region ORGANIZATIONS

        getOrganizations: build.query<IOrganizationRef[], {username: string}>({
            query: (arg) => `settings/organization/refs/${arg.username}`,
            transformResponse: (response: IGetOrganizationRefsResponse) => response.organizations,
            providesTags: (result) => result
                ? [...result.map(o => ({type: ORGANIZATION_TYPE, id: o.id} as const)), {
                    type: ORGANIZATION_TYPE,
                    id: LIST
                }]
                : [{type: ORGANIZATION_TYPE, id: LIST}],
        }),
        getOrganization: build.query<IOrganization, string>({
            query: (arg) => `settings/organizations/${arg}`
        }),
        updateOrganization: build.mutation<IActionResponse, {request: IUpdateOrganizationRequest, id: string}>({
            query: (arg) => ({
                url: `settings/organization/${arg.id}`,
                method: 'PATCH',
                body: arg.request,
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResults = dispatch(
                    organizationsApi.util.updateQueryData('getOrganization', arg.id, (draft) => {
                        const orgCopy = cloneDeep(draft)
                        orgCopy.name = arg.request.name ?? "";
                        orgCopy.logo = arg.request.logo;
                        orgCopy.country = arg.request.country;
                        orgCopy.defaultCatalog = arg.request.defaultCatalog;
                        orgCopy.punchoutCurrency = arg.request.punchoutCurrency;
                        Object.assign(draft, orgCopy);
                    })
                );
                
                try {
                    await queryFulfilled
                }
                catch {
                    patchResults.undo()
                }
            }
        }),
        getOrganizationFeatureSettings: build.query<IOrganizationFeatureSettings, string>({
            query: (arg) => `settings/organization/${arg}/features`,
        }),
        /** Queues a recalculation of every product's purchase counters over the configured window. */
        rebuildPurchaseStats: build.mutation<IActionResponse, void>({
            query: () => ({url: 'catalog/purchaseStats/rebuild', method: 'POST'}),
        }),
        updateOrganizationFeatureSettings: build.mutation<IActionResponse, {request: IOrganizationFeatureSettings, id: string}>({
            query: (arg) => ({
                url: `settings/organization/${arg.id}/features`,
                method: 'PATCH',
                body: arg.request,
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResults = dispatch(
                    organizationsApi.util.updateQueryData('getOrganizationFeatureSettings', arg.id, (draft) => {
                        const orgCopy = cloneDeep(draft)
                        orgCopy.enableDynamicProductListings = arg.request.enableDynamicProductListings;
                        orgCopy.enableSimpleTaxCalculation = arg.request.enableSimpleTaxCalculation;
                        orgCopy.purchaseStatsMonths = arg.request.purchaseStatsMonths;
                        orgCopy.dynamicProductListingUpdateSchedule = arg.request.dynamicProductListingUpdateSchedule;
                        orgCopy.id = arg.id
                        Object.assign(draft, orgCopy);
                    })
                );

                try {
                    await queryFulfilled
                }
                catch {
                    patchResults.undo()
                }
            }
        }),
        getOrganizationProcurementSettings: build.query<IOrganizationProcurementSettings, string>({
            query: (arg) => `settings/organization/${arg}/procurement`,
        }),
        updateOrganizationProcurementSettings: build.mutation<IActionResponse, {request: IOrganizationProcurementSettings, id: string}>({
            query: (arg) => ({
                url: `settings/organization/${arg.id}/procurement`,
                method: 'PATCH',
                body: arg.request,
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const patchResults = dispatch(
                    organizationsApi.util.updateQueryData('getOrganizationProcurementSettings', arg.id, (draft) => {
                        const orgCopy = cloneDeep(draft)
                        orgCopy.contactFirstName = arg.request.contactFirstName;
                        orgCopy.contactLastName = arg.request.contactLastName;
                        orgCopy.contactEmail = arg.request.contactEmail;
                        orgCopy.contactPhone = arg.request.contactPhone;
                        orgCopy.address1 = arg.request.address1;
                        orgCopy.address2 = arg.request.address2;
                        orgCopy.city = arg.request.city;
                        orgCopy.stateProvince = arg.request.stateProvince;
                        orgCopy.postalCode = arg.request.postalCode;
                        orgCopy.id = arg.id
                        Object.assign(draft, orgCopy);
                    })
                );

                try {
                    await queryFulfilled
                }
                catch {
                    patchResults.undo()
                }
            }
        }),
        getOrganizationEntitlements: build.query<IOrganizationEntitlements, string>({
            query: (arg) => `settings/organization/${arg}/entitlements`,
            // Tagged so custom-domain verification/removal can refresh the domain dropdown's options.
            providesTags: [ORGANIZATION_ENTITLEMENTS_TYPE],
        }),
        setSelectedOrganization: build.mutation<null, string | undefined>({
            queryFn: (arg, {dispatch}) => {
                dispatch(setSelectedOrganization(arg));
                return {data: null};
            },
            invalidatesTags: [
                {type: WORKSPACES_TYPE, id: LIST},
                {type: CATALOG_TYPE, id: LIST},
                {type: COLUMNS_TYPE, id: LIST},
                {type: WORKSPACE_PRODUCTS_TYPE, id: LIST},
                {type: FAVORITES_TYPE, id: LIST},
                {type: SEARCH_RESULTS_TYPE, id: LIST},
            ]
        }),
        //endregion
    }),
    overrideExisting: false,
})

export const {
    useRebuildPurchaseStatsMutation,
    useGetOrganizationsQuery,
    useGetOrganizationQuery,
    useUpdateOrganizationMutation,
    useGetOrganizationFeatureSettingsQuery,
    useUpdateOrganizationFeatureSettingsMutation,
    useSetSelectedOrganizationMutation,
    useLazyGetOrganizationsQuery,
    useLazyGetOrganizationQuery,
    useLazyGetOrganizationEntitlementsQuery,
    useGetOrganizationEntitlementsQuery,
    useUpdateOrganizationProcurementSettingsMutation,
    useGetOrganizationProcurementSettingsQuery,
    
} = organizationsApi
